/**
 * useCountdown — drives a live mm:ss countdown to a wall-clock target.
 *
 * Intentionally minimal: no requestAnimationFrame fanciness, no sub-
 * second precision. We tick once per second, which is correct for a
 * session-expiry display where the user only cares about minute-level
 * granularity.
 *
 * Behavioural contract:
 *   - `remainingMs` is clamped to ≥ 0 — never negative.
 *   - `expired` flips true the moment `Date.now() >= targetMs`.
 *   - The interval is cleared on unmount or when `targetMs` changes
 *     (e.g. agent issues a new code via "Copy New").
 *   - When the tab is backgrounded, `setInterval` may throttle to
 *     ~1Hz at best. That's fine — the user re-checks on focus and
 *     `Date.now()` is the source of truth, not the tick count.
 *
 * Skew tolerance: the server-issued `expiresAt` is UTC ms epoch. The
 * client compares against `Date.now()` which is also UTC ms. A few
 * seconds of clock skew between client + server is harmless for a
 * minute-level display.
 */
import { useEffect, useState } from 'react'

export interface CountdownState {
  /** Milliseconds until the target, clamped to ≥ 0. */
  readonly remainingMs: number
  /** True iff `Date.now() >= targetMs`. */
  readonly expired: boolean
  /** Pre-formatted "M:SS" string (e.g. "9:04", "0:30", "0:00"). */
  readonly formatted: string
}

export function useCountdown(targetMs: number | null | undefined): CountdownState {
  const compute = (): CountdownState => {
    if (targetMs == null) {
      return { remainingMs: 0, expired: true, formatted: '0:00' }
    }
    const remainingMs = Math.max(0, targetMs - Date.now())
    return {
      remainingMs,
      expired: remainingMs <= 0,
      formatted: formatMmss(remainingMs),
    }
  }

  const [state, setState] = useState<CountdownState>(compute)

  useEffect(() => {
    if (targetMs == null) {
      setState({ remainingMs: 0, expired: true, formatted: '0:00' })
      return
    }
    // Initial sync — `compute()` was called from initial state, but
    // target may have changed between render and effect run.
    setState(compute())
    const id = setInterval(() => setState(compute()), 1000)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetMs])

  return state
}

/**
 * "M:SS" — minutes uncapped (a 30-min TTL formats as "30:00"; we don't
 * use H:MM:SS because session TTLs are bounded at 30 min by the
 * service layer).
 */
function formatMmss(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}
